import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

const submittedPanelSx = {
  mt: 1,
  p: 1.5,
  width: "100%",
  boxSizing: "border-box",
  borderRadius: "18px",
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.035))",
  border: "1px solid rgba(219,165,41,0.26)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.12), 0 14px 34px rgba(0,0,0,0.18)",
  backdropFilter: "blur(18px) saturate(120%)",
  WebkitBackdropFilter: "blur(18px) saturate(120%)",
};

const notePanelSx = {
  mt: 1,
  p: { xs: 1.6, sm: 1.9 },
  width: "min(390px, 100%)",
  boxSizing: "border-box",
  position: "relative",
  borderRadius: "24px",
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.035))",
  border: "1px solid rgba(219,165,41,0.26)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.12), 0 14px 34px rgba(0,0,0,0.18)",
  backdropFilter: "blur(18px) saturate(120%)",
  WebkitBackdropFilter: "blur(18px) saturate(120%)",
};

const actionButtonSx = {
  minWidth: 0,
  px: 0.25,
  color: "rgba(219,165,41,0.95)",
  fontSize: 12,
  textTransform: "none",
  "&:hover": {
    bgcolor: "rgba(219,165,41,0.08)",
  },
};

const noteActionButtonSx = {
  minWidth: 0,
  px: 0.25,
  py: 0.35,
  color: "rgba(246,192,78,0.96)",
  fontSize: 12,
  textTransform: "none",
  "& .MuiButton-startIcon": {
    mr: 0.75,
    color: "inherit",
  },
  "&:hover": {
    bgcolor: "rgba(219,165,41,0.1)",
  },
};

const noteRemoveButtonSx = {
  ...noteActionButtonSx,
  color: "rgba(255,129,105,0.96)",
  "&:hover": {
    bgcolor: "rgba(255,129,105,0.1)",
  },
};

const noteCancelButtonSx = {
  ...noteActionButtonSx,
  color: "rgba(234,231,226,0.72)",
  "&:hover": {
    bgcolor: "rgba(255,255,255,0.08)",
  },
};

const removeButtonSx = {
  ...actionButtonSx,
  color: "rgba(255,154,154,0.92)",
  "&:hover": {
    bgcolor: "rgba(255,154,154,0.08)",
  },
};

const NOTE_PREVIEW_LIMIT = 150;
const NOTE_PREVIEW_LINE_LIMIT = 3;

const parseLegacyNoteString = (value) => {
  const body = value.replace(/^\{|\}$/g, "");
  const fields = ["note", "userName", "userId"];
  const readField = (field) => {
    const marker = `${field}=`;
    const start = body.indexOf(marker);
    if (start === -1) return "";

    const after = body.slice(start + marker.length);
    const nextStarts = fields
      .filter((item) => item !== field)
      .map((item) => after.search(new RegExp(`,\\s*${item}=`)))
      .filter((index) => index >= 0);
    const end = nextStarts.length ? Math.min(...nextStarts) : after.length;

    return after.slice(0, end).replace(/^,|,$/g, "").trim();
  };

  const noteText = readField("note");
  if (!noteText) return null;

  return {
    note: noteText,
    userName: readField("userName"),
    userId: readField("userId"),
  };
};

export const normalizeFeedbackEntry = (feedback) => {
  if (!feedback) return null;
  if (typeof feedback === "string") {
    return feedback ? { rating: feedback, comment: "" } : null;
  }

  const rating = feedback.rating ?? feedback.Rating ?? null;
  if (!rating) return null;

  return {
    rating,
    comment: String(feedback.comment ?? feedback.Comment ?? "").trim(),
  };
};

export const getFeedbackRating = (feedback) =>
  normalizeFeedbackEntry(feedback)?.rating ?? null;

export const normalizeNoteEntry = (note) => {
  if (!note) return null;

  if (typeof note === "string") {
    const value = note.trim();
    if (!value) return null;

    if (/^\{.*\}$/.test(value) && value.includes("note=")) {
      return parseLegacyNoteString(value);
    }

    return {
      note: value,
      userName: "",
      userId: "",
    };
  }

  const rawNote =
    note.note ??
    note.Note ??
    note.notes ??
    note.Notes ??
    note.noteText ??
    note.NoteText ??
    note.messageNote ??
    note.MessageNote ??
    note.messageNotes ??
    note.MessageNotes ??
    note.message_note ??
    "";
  const parsedNestedNote =
    rawNote && typeof rawNote === "object"
      ? normalizeNoteEntry(rawNote)
      : typeof rawNote === "string"
        ? normalizeNoteEntry(rawNote)
        : null;
  const primitiveNote =
    typeof rawNote === "string" || typeof rawNote === "number" ? rawNote : "";
  const noteText = String(parsedNestedNote?.note ?? primitiveNote).trim();
  if (!noteText) return null;

  return {
    note: noteText,
    userName: String(
      note.userName ??
        note.UserName ??
        note.user_name ??
        note.createdBy ??
        note.CreatedBy ??
        note.created_by ??
        parsedNestedNote?.userName ??
        "",
    ).trim(),
    userId: String(
      note.userId ??
        note.UserId ??
        note.user_id ??
        note.createdById ??
        note.CreatedById ??
        note.created_by_id ??
        parsedNestedNote?.userId ??
        "",
    ).trim(),
  };
};

export default function SubmittedFeedback({
  feedback,
  note,
  canManageNote = false,
  disabled = false,
  children,
  trailingActions = null,
  onEdit,
  onRemove,
  onSaveNote,
  onRemoveNote,
}) {
  const savedFeedback = useMemo(
    () => normalizeFeedbackEntry(feedback),
    [feedback],
  );
  const savedNote = useMemo(() => normalizeNoteEntry(note), [note]);
  const [open, setOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteViewOpen, setNoteViewOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(savedNote?.note ?? "");

  useEffect(() => {
    setOpen(false);
  }, [savedFeedback?.rating, savedFeedback?.comment]);

  useEffect(() => {
    setNoteDraft(savedNote?.note ?? "");
    if (!savedNote) {
      setNoteOpen(false);
      setNoteViewOpen(false);
    }
  }, [savedNote?.note]);

  const isPositiveFeedback = savedFeedback?.rating === "Good";
  const reviewIconColor = "rgba(255,255,255,0.7)";
  const toggleFeedback = () => setOpen((prev) => !prev);
  const noteAuthorLabel = canManageNote
    ? "You"
    : savedNote?.userName || "Note";
  const savedNoteText = savedNote?.note ?? "";
  const noteLineCount = savedNoteText.split(/\r\n|\r|\n/).length;
  const isLongNote =
    savedNoteText.length > NOTE_PREVIEW_LIMIT ||
    noteLineCount > NOTE_PREVIEW_LINE_LIMIT;
  const notePreviewText = isLongNote
    ? `${savedNoteText.slice(0, NOTE_PREVIEW_LIMIT).trimEnd()}...`
    : savedNoteText;
  const canSaveNote = Boolean(onSaveNote) && noteDraft.trim().length > 0;
  const noteIconActive = Boolean(
    savedNote || noteOpen || noteViewOpen || noteDialogOpen,
  );
  const toggleNote = () => {
    if (savedNote) {
      setNoteOpen((prev) => !prev);
      return;
    }

    if (disabled || !onSaveNote) return;
    setNoteDraft("");
    setNoteDialogOpen(true);
  };
  const handleEditNote = () => {
    setNoteDraft(savedNote?.note ?? "");
    setNoteViewOpen(false);
    setNoteDialogOpen(true);
  };
  const handleCancelNote = () => {
    setNoteDraft(savedNote?.note ?? "");
    setNoteDialogOpen(false);
  };
  const handleSaveNote = async () => {
    if (!canSaveNote || disabled) return;
    const saved = await onSaveNote(noteDraft.trim());
    if (saved !== false) {
      setNoteDialogOpen(false);
      setNoteOpen(true);
    }
  };
  const handleRemoveNote = async () => {
    if (!onRemoveNote || disabled) return;
    const removed = await onRemoveNote();
    if (removed !== false) {
      setNoteDraft("");
      setNoteOpen(false);
      setNoteViewOpen(false);
      setNoteDialogOpen(false);
    }
  };
  const actionChildren =
    typeof children === "function"
      ? children({
          isFeedbackOpen: open,
          toggleFeedback,
        })
      : children;

  return (
    <Box sx={{ mt: 0.75, width: "min(540px, 100%)" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
        }}
      >
        {actionChildren}
        <Tooltip title={savedNote ? "View note" : "Add note"}>
          <span>
            <IconButton
              size="small"
              onClick={toggleNote}
              disabled={disabled && !savedNote}
              aria-label={savedNote ? "View note" : "Add note"}
              sx={{
                ml: 0.25,
                color: noteIconActive ? "rgba(246,192,78,0.98)" : reviewIconColor,
                bgcolor: noteIconActive ? "rgba(219,165,41,0.14)" : "transparent",
                border: noteIconActive
                  ? "1px solid rgba(219,165,41,0.42)"
                  : "1px solid transparent",
                boxShadow: noteIconActive
                  ? "0 0 18px rgba(219,165,41,0.18)"
                  : "none",
                "&:hover": {
                  bgcolor: noteIconActive
                    ? "rgba(219,165,41,0.2)"
                    : "rgba(255,255,255,0.06)",
                },
              }}
            >
              <RateReviewOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {trailingActions}
      </Box>

      {open && savedFeedback && (
        <Box sx={submittedPanelSx}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              mb: 0.75,
            }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.92)",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Your Feedback
            </Typography>
            <Box
              sx={{
                px: 1.1,
                py: 0.35,
                borderRadius: "999px",
                color: isPositiveFeedback ? "#7CFC98" : "#FFB0A8",
                bgcolor: isPositiveFeedback
                  ? "rgba(124,252,152,0.12)"
                  : "rgba(255,154,154,0.12)",
                border: `1px solid ${
                  isPositiveFeedback
                    ? "rgba(124,252,152,0.28)"
                    : "rgba(255,154,154,0.28)"
                }`,
                fontSize: 11.5,
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {isPositiveFeedback ? "Positive" : "Needs work"}
            </Box>
          </Box>
          <Typography
            sx={{
              color: "rgba(234,231,226,0.82)",
              fontSize: 13.2,
              lineHeight: 1.5,
            }}
          >
            {savedFeedback.comment ||
              (isPositiveFeedback
                ? "You marked this response as helpful."
                : "You marked this response as not helpful.")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1,
            }}
          >
            <Button
              size="small"
              onClick={onEdit}
              disabled={disabled}
              sx={actionButtonSx}
            >
              Edit feedback
            </Button>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: "rgba(255,255,255,0.16)" }}
            />
            <Button
              size="small"
              onClick={onRemove}
              disabled={disabled}
              sx={removeButtonSx}
            >
              Remove
            </Button>
          </Box>
        </Box>
      )}

      {noteOpen && savedNote && (
        <Box sx={notePanelSx}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              mb: 0.75,
            }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.92)",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Notes
            </Typography>
            <Box
              sx={{
                px: 1.1,
                py: 0.45,
                borderRadius: "999px",
                color: "rgba(255,243,212,0.98)",
                background:
                  "linear-gradient(135deg, rgba(219,165,41,0.58), rgba(126,84,20,0.68))",
                border: "1px solid rgba(255,214,124,0.24)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)",
                fontSize: 11.5,
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {noteAuthorLabel}
            </Box>
          </Box>

          <Typography
            sx={{
              color: "rgba(234,231,226,0.82)",
              fontSize: 13.2,
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              mt: 1,
            }}
          >
            {notePreviewText}
          </Typography>

          {isLongNote && (
            <Button
              size="small"
              onClick={() => setNoteViewOpen(true)}
              sx={{
                ...noteActionButtonSx,
                mt: 0.5,
                px: 0,
              }}
            >
              View more
            </Button>
          )}

          {canManageNote && (
            <>
              <Divider
                sx={{
                  borderColor: "rgba(255,255,255,0.14)",
                  mt: isLongNote ? 1 : 2.2,
                  mb: 1,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.1,
                }}
              >
                <Button
                  size="small"
                  startIcon={<EditOutlinedIcon fontSize="small" />}
                  onClick={handleEditNote}
                  disabled={disabled}
                  sx={noteActionButtonSx}
                >
                  Edit note
                </Button>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: "rgba(255,255,255,0.18)" }}
                />
                <Button
                  size="small"
                  startIcon={<DeleteOutlineRoundedIcon fontSize="small" />}
                  onClick={handleRemoveNote}
                  disabled={disabled}
                  sx={noteRemoveButtonSx}
                >
                  Remove
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      <Dialog
        open={noteViewOpen}
        onClose={() => setNoteViewOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: 680,
            maxWidth: "94vw",
            borderRadius: "24px",
            color: "white",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.035))",
            border: "1px solid rgba(219,165,41,0.26)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.12), 0 22px 64px rgba(0,0,0,0.34)",
            backdropFilter: "blur(18px) saturate(120%)",
            WebkitBackdropFilter: "blur(18px) saturate(120%)",
            maxHeight: "calc(100vh - 64px)",
            "@media (max-width: 767px)": {
              maxHeight: "calc(100dvh - 64px)",
            },
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
              Notes
            </Typography>
            <Box
              sx={{
                px: 1.1,
                py: 0.45,
                borderRadius: "999px",
                color: "rgba(255,243,212,0.98)",
                background:
                  "linear-gradient(135deg, rgba(219,165,41,0.58), rgba(126,84,20,0.68))",
                border: "1px solid rgba(255,214,124,0.24)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)",
                fontSize: 11.5,
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {noteAuthorLabel}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mt: 1,
              maxHeight: "calc(100vh - 230px)",
              "@media (max-width: 767px)": {
                maxHeight: "calc(100dvh - 230px)",
              },
              overflowY: "auto",
              pr: 0.75,
              color: "rgba(234,231,226,0.86)",
              fontSize: 14,
              lineHeight: 1.65,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              "&::-webkit-scrollbar": {
                width: 5,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(255,255,255,0.24)",
                borderRadius: "999px",
              },
            }}
          >
            {savedNoteText}
          </Box>
        </DialogContent>
        <Divider sx={{ mx: 3, borderColor: "rgba(255,255,255,0.14)" }} />
        <DialogActions
          sx={{
            px: 3,
            py: 1.4,
            justifyContent: "flex-start",
            gap: 1.5,
          }}
        >
          {canManageNote && (
            <>
              <Button
                size="small"
                startIcon={<EditOutlinedIcon fontSize="small" />}
                onClick={handleEditNote}
                disabled={disabled}
                sx={noteActionButtonSx}
              >
                Edit note
              </Button>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: "rgba(255,255,255,0.18)" }}
              />
              <Button
                size="small"
                startIcon={<DeleteOutlineRoundedIcon fontSize="small" />}
                onClick={handleRemoveNote}
                disabled={disabled}
                sx={noteRemoveButtonSx}
              >
                Remove
              </Button>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: "rgba(255,255,255,0.18)" }}
              />
            </>
          )}
          <Button
            size="small"
            startIcon={<CloseRoundedIcon fontSize="small" />}
            onClick={() => setNoteViewOpen(false)}
            sx={noteCancelButtonSx}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noteDialogOpen}
        onClose={disabled ? undefined : handleCancelNote}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            width: 430,
            maxWidth: "92vw",
            borderRadius: "24px",
            color: "white",
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.035))",
            border: "1px solid rgba(219,165,41,0.26)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.12), 0 14px 34px rgba(0,0,0,0.18)",
            backdropFilter: "blur(18px) saturate(120%)",
            WebkitBackdropFilter: "blur(18px) saturate(120%)",
            maxHeight: "calc(100vh - 64px)",
            "@media (max-width: 767px)": {
              maxHeight: "calc(100dvh - 64px)",
            },
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, fontWeight: 800 }}>
          {savedNote ? "Edit note" : "Add note"}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              color: "rgba(234,231,226,0.68)",
              fontSize: 13,
              mb: 1.5,
            }}
          >
            Save a quick note for this response.
          </Typography>
          <TextField
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            placeholder="Write your note..."
            fullWidth
            multiline
            minRows={8}
            maxRows={8}
            autoFocus
            disabled={disabled}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "rgba(250,246,239,0.94)",
                fontSize: 14,
                borderRadius: "16px",
                bgcolor: "rgba(255,255,255,0.055)",
                "& fieldset": {
                  borderColor: "rgba(255,255,255,0.16)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(219,165,41,0.42)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(219,165,41,0.7)",
                },
              },
              "& .MuiInputBase-inputMultiline": {
                overflowY: "auto !important",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                pr: 0.75,
                "&::-webkit-scrollbar": {
                  width: 5,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255,255,255,0.24)",
                  borderRadius: "999px",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "rgba(234,231,226,0.46)",
                opacity: 1,
              },
            }}
          />
        </DialogContent>
        <Divider sx={{ mx: 3, borderColor: "rgba(255,255,255,0.14)" }} />
        <DialogActions
          sx={{
            px: 3,
            py: 1.4,
            justifyContent: "flex-start",
            gap: 1.5,
          }}
        >
          <Button
            size="small"
            startIcon={<SaveOutlinedIcon fontSize="small" />}
            onClick={handleSaveNote}
            disabled={disabled || !canSaveNote}
            sx={noteActionButtonSx}
          >
            Save note
          </Button>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "rgba(255,255,255,0.18)" }}
          />
          <Button
            size="small"
            startIcon={<CloseRoundedIcon fontSize="small" />}
            onClick={handleCancelNote}
            disabled={disabled}
            sx={noteCancelButtonSx}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
